<template>
  <div
    v-show="isVisible"
    ref="rootRef"
    class="aira-widget"
    :class="{
      'aira-widget--flying': isFlying,
      'aira-widget--approaching': isLookingAhead,
    }"
    :style="rootStyle"
  >
    <transition name="aira-bubble">
      <p v-if="hint" class="aira-widget__bubble">{{ hint }}</p>
    </transition>

    <button
      class="aira-widget__button"
      :class="{ 'aira-widget__button--shaking': isShaking }"
      type="button"
      aria-label="Aira"
      @click="openAssistant"
    >
      <canvas ref="canvasRef" class="aira-widget__canvas" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import type { Rive, ViewModelInstance } from "@rive-app/webgl2";
import { useAiraWidget } from "../store/useAiraWidgetStore";
import { useAiraGaze } from "../composables/useAiraGaze";
import type { AiraPropertyValue } from "../types";

const props = withDefaults(defineProps<{ gaze?: boolean }>(), { gaze: true });

const RIVE_SRC = "/rive/aira-contextual.riv";
const STATE_MACHINE = "State Machine 1";

const router = useRouter();
const store = useAiraWidget();
const { isVisible, hint, anchor, gazeTarget, isShaking, scale, isLookingAhead } =
  storeToRefs(store);

const canvasRef = ref<HTMLCanvasElement | null>(null);
const rootRef = ref<HTMLElement | null>(null);

/** Offset from the home corner, in px. Driven by the store's anchor point. */
const offset = ref({ x: 0, y: 0 });
const isFlying = computed(() => offset.value.x !== 0 || offset.value.y !== 0);

const rootStyle = computed(() => ({
  transform: `translate3d(${offset.value.x}px, ${offset.value.y}px, 0) scale(${scale.value})`,
}));

// The widget is positioned by `right`/`bottom`, so flying means translating away
// from wherever the layout put it. Subtracting the current offset recovers the home
// centre, which keeps repeated flights from compounding.
watch(anchor, (point) => {
  if (!point || !rootRef.value) {
    offset.value = { x: 0, y: 0 };
    return;
  }

  const rect = rootRef.value.getBoundingClientRect();
  const homeX = rect.left + rect.width / 2 - offset.value.x;
  const homeY = rect.top + rect.height / 2 - offset.value.y;

  offset.value = { x: point.x - homeX, y: point.y - homeY };
});
const rive = shallowRef<Rive | null>(null);
const viewModel = shallowRef<ViewModelInstance | null>(null);

let resizeObserver: ResizeObserver | null = null;

// Follow the cursor across the whole viewport, not just over the canvas.
useAiraGaze(canvasRef, {
  enabled: computed(() => props.gaze && isVisible.value),
  target: gazeTarget,
});

/**
 * Write a data binding property, or fire it as a trigger when no value is given.
 * Unknown paths resolve to null and are ignored, so the widget keeps working
 * against artboards that do not expose the full view model yet.
 */
function write(path: string, value?: AiraPropertyValue) {
  const instance = viewModel.value;
  if (!instance) return;

  if (value === undefined) {
    instance.trigger(path)?.trigger();
    return;
  }

  if (typeof value === "boolean") {
    const property = instance.boolean(path);
    if (property) property.value = value;
    return;
  }

  if (typeof value === "number") {
    const property = instance.number(path);
    if (property) property.value = value;
    return;
  }

  const property = instance.string(path);
  if (property) property.value = value;
}

function propertyNames() {
  return (viewModel.value?.properties ?? []).map((property) => property.name);
}

onMounted(async () => {
  if (!canvasRef.value) return;

  // Rive runtime touches window/canvas, so it is imported only on the client.
  //
  // It must stay @rive-app/webgl2: blur is drawn by the Rive Renderer, and the
  // lighter @rive-app/canvas (Canvas2D) drops it silently — no warning, the artboard
  // just renders sharp. The heavier wasm is the price of the artboard's blur.
  const { Rive: RiveCtor, Layout, Fit, Alignment } = await import("@rive-app/webgl2");

  rive.value = new RiveCtor({
    src: RIVE_SRC,
    canvas: canvasRef.value,
    autoplay: true,
    stateMachines: STATE_MACHINE,
    // Binds the artboard's default view model instance — the replacement for the
    // now deprecated state machine inputs.
    autoBind: true,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    onLoad: () => {
      rive.value?.resizeDrawingSurfaceToCanvas();
      viewModel.value = rive.value?.viewModelInstance ?? null;
      store.registerController({ write, propertyNames });
      if (import.meta.env.DEV) {
        console.info("[aira] view model properties:", propertyNames());
      }
    },
  });

  resizeObserver = new ResizeObserver(() => {
    rive.value?.resizeDrawingSurfaceToCanvas();
  });
  resizeObserver.observe(canvasRef.value);
});

/**
 * While walking into the camera her eyes must read as looking at the viewer, so the
 * gaze target is pinned to her own centre — which keeps moving as she grows, hence
 * the per-frame update.
 */
let aheadFrame = 0;

/** Overshoot past the viewport so she visibly walks out of the screen, not up to it. */
const APPROACH_OVERSHOOT = 2.6;

watch(isLookingAhead, (ahead) => {
  if (!ahead) {
    if (aheadFrame) cancelAnimationFrame(aheadFrame);
    aheadFrame = 0;
    store.lookAtCursor();
    return;
  }

  // Scale is derived from the rendered size, so the finale fills any viewport
  // instead of relying on a fixed multiplier that is huge on a phone and tame on a 5K.
  const rect = rootRef.value?.getBoundingClientRect();
  const base = rect ? Math.max(rect.width, rect.height) / scale.value : 96;
  const longest = Math.max(window.innerWidth, window.innerHeight);
  store.setScale((longest / base) * APPROACH_OVERSHOOT);

  const aim = () => {
    const canvas = canvasRef.value;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      store.lookAt({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    }
    aheadFrame = requestAnimationFrame(aim);
  };
  aim();
});

onBeforeUnmount(() => {
  if (aheadFrame) cancelAnimationFrame(aheadFrame);
  aheadFrame = 0;
  resizeObserver?.disconnect();
  resizeObserver = null;
  store.unregisterController();
  viewModel.value = null;
  rive.value?.cleanup();
  rive.value = null;
});

function openAssistant() {
  router.push({ name: "chatbot" });
}
</script>

<style scoped>
.aira-widget {
  position: fixed;
  transform-origin: center center;
  right: 24px;
  bottom: 24px;
  z-index: 1100;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  transition: transform 900ms cubic-bezier(0.34, 1.12, 0.64, 1);
  will-change: transform;
}

.aira-widget--flying {
  /* Above the page content she is hovering over, below modals. */
  z-index: 1150;
}

/* The approach is slow and deliberate, and has to sit above everything. */
.aira-widget--approaching {
  z-index: 1900;
  transition: transform 2600ms cubic-bezier(0.5, 0, 0.75, 0);
}

@media (prefers-reduced-motion: reduce) {
  .aira-widget {
    transition-duration: 1ms;
  }
}

.aira-widget__bubble {
  max-width: 220px;
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: 16px 16px 4px 16px;
  background: var(--fair-surface, #fff);
  box-shadow: 0 8px 24px rgb(0 0 0 / 12%);
  font-size: 13px;
  line-height: 1.35;
  color: var(--fair-text, #111);
}

.aira-widget__button {
  width: 96px;
  height: 96px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

/* The wobble lives on the button: the root element's transform is owned by the
   flight animation, and a second transform there would cancel it out. */
.aira-widget__button--shaking {
  animation: aira-widget-shake 140ms linear infinite;
}

@keyframes aira-widget-shake {
  0% { transform: translate3d(-3px, 0, 0) rotate(-3deg); }
  25% { transform: translate3d(3px, -1px, 0) rotate(3deg); }
  50% { transform: translate3d(-2px, 1px, 0) rotate(-2deg); }
  75% { transform: translate3d(2px, -1px, 0) rotate(2deg); }
  100% { transform: translate3d(-3px, 0, 0) rotate(-3deg); }
}

@media (prefers-reduced-motion: reduce) {
  .aira-widget__button--shaking {
    animation: none;
  }
}

.aira-widget__canvas {
  width: 100%;
  height: 100%;
  display: block;
  /* Rive must only ever see the remapped events from useAiraGaze — native pointer
     input would fight it and snap the gaze whenever the cursor crossed the canvas. */
  pointer-events: none;
}

.aira-bubble-enter-active,
.aira-bubble-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.aira-bubble-enter-from,
.aira-bubble-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (max-width: 1023px) {
  .aira-widget {
    right: 16px;
    bottom: 88px; /* clear the mobile bottom dock */
  }

  .aira-widget__bubble {
    max-width: 160px;
  }

  .aira-widget__button {
    width: 72px;
    height: 72px;
  }
}
</style>
